import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import clsx from 'clsx'

import useCookieConsent from '@/hooks/useCookieConsent'
import ConsentModal from './ConsentModal'

// Stable outside component — useSyncExternalStore requires a stable subscribe reference
const subscribe = () => () => {}

/**
 * @component CookieBanner
 * @description Fixed cookie consent banner shown on first visit. Manages the consent modal.
 * Publishes --cookie-banner-h CSS variable so sibling components can offset themselves.
 */
const CookieBanner = () => {
	// Returns false on SSR / hydration, true on client — avoids localStorage hydration mismatch
	const isClient = useSyncExternalStore(subscribe, () => true, () => false)
	const [modalOpen, setModalOpen] = useState(false)
	const { consent, acceptAll, saveCustom } = useCookieConsent()
	const bannerRef = useRef<HTMLDivElement>(null)

	// Publish banner height as CSS variable so BackToTop can offset above it on mobile
	useEffect(() => {
		if (!isClient || consent.decided) return
		const el = bannerRef.current
		if (!el) return
		const sync = () =>
			document.documentElement.style.setProperty('--cookie-banner-h', `${el.offsetHeight}px`)
		sync()
		window.addEventListener('resize', sync)
		return () => {
			window.removeEventListener('resize', sync)
			document.documentElement.style.removeProperty('--cookie-banner-h')
		}
	}, [isClient, consent.decided])

	if (!isClient || consent.decided) return null

	return (
		<>
			<div
				ref={ bannerRef }
				className={clsx(
					'fixed bottom-0 left-0 right-0 z-40',
					'rounded-t-none bg-white px-6 pt-6 pb-8',
					'shadow-[0_-2px_20px_rgba(0,0,0,0.07)]',
					'flex flex-col gap-4',
					'sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto',
					'sm:w-[calc(100vw-3rem)] sm:max-w-150',
					'sm:rounded-2xl sm:shadow-lg sm:p-6',
					'sm:flex-row sm:justify-between sm:items-center sm:gap-6'
				)}
			>
				<div className="flex flex-col">
					<p className="primary/small font-medium text-gray-800">เราใช้คุกกี้เพื่อประสบการณ์ที่ดีของคุณ</p>
					<p className="primary/paragraph font-normal text-gray-400">
						{'คุณสามารถเลือก '}
						<button
							type="button"
							onClick={ () => setModalOpen(true) }
							className="primary/paragraph font-normal text-gray-400 cursor-pointer underline"
						>
							ตั้งค่าความยินยอม
						</button>
						{' ได้'}
					</p>
				</div>
				<button
					type="button"
					onClick={ acceptAll }
					className={clsx(
						'flex items-center justify-center h-12 w-full rounded-full bg-gray-800 primary/small font-medium text-white cursor-pointer transition-colors',
						'hover:bg-gray-700',
						'sm:w-fit sm:px-6'
					)}
				>
					<span className="primary/small font-medium">ยอมรับทั้งหมด</span>
				</button>
			</div>

			{ modalOpen && (
				<ConsentModal
					consent={ consent }
					onClose={ () => setModalOpen(false) }
					onAcceptAll={ acceptAll }
					onSaveCustom={ saveCustom }
				/>
			) }
		</>
	)
}

export default CookieBanner
